import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
}

let nextItemId = 2;

export function InvoiceCreatePage() {
  const [client, setClient] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: '1', description: '', quantity: 1, rate: 0 },
  ]);

  const addLineItem = () => {
    setLineItems([...lineItems, { id: String(nextItemId++), description: '', quantity: 1, rate: 0 }]);
  };

  const removeLineItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((item) => item.id !== id));
    }
  };

  const updateLineItem = (id: string, field: keyof LineItem, value: string | number) => {
    setLineItems(
      lineItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.rate, 0);
  const tax = subtotal * 0.08;
  const total = subtotal + tax;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // API integration will be added later
  };

  const inputStyle = {
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text)',
    fontSize: '0.875rem',
    width: '100%',
  } as const;

  const labelStyle = {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 500,
    marginBottom: '6px',
    color: 'var(--color-text-muted)',
  } as const;

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '24px', fontSize: '0.875rem' }}>
        <Link to="/invoices" style={{ color: 'var(--color-text-muted)' }}>
          Invoices
        </Link>
        <span style={{ margin: '0 8px', color: 'var(--color-text-muted)' }}>/</span>
        <span>New Invoice</span>
      </div>

      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '24px' }}>
        Create Invoice
      </h1>

      <form onSubmit={handleSubmit}>
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '20px' }}>
            Invoice Details
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Client</label>
              <select
                value={client}
                onChange={(e) => setClient(e.target.value)}
                style={inputStyle}
              >
                <option value="">Select a client...</option>
                <option value="1">Horizon Films - Sarah Chen</option>
                <option value="2">Vertex Productions - Marcus Rivera</option>
                <option value="3">Bloom Media - Emily Watson</option>
                <option value="4">Crestline Studios - Jake Thornton</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '20px' }}>
            Line Items
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Header row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 120px 100px 40px', gap: '8px', fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 4px' }}>
              <span>Description</span>
              <span>Qty</span>
              <span>Rate</span>
              <span>Total</span>
              <span />
            </div>

            {lineItems.map((item) => (
              <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 120px 100px 40px', gap: '8px', alignItems: 'center' }}>
                <input
                  placeholder="e.g., Video editing - 8 hours"
                  value={item.description}
                  onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                  style={inputStyle}
                />
                <input
                  type="number"
                  min={1}
                  value={item.quantity}
                  onChange={(e) => updateLineItem(item.id, 'quantity', Number(e.target.value))}
                  style={inputStyle}
                />
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={item.rate || ''}
                  onChange={(e) => updateLineItem(item.id, 'rate', Number(e.target.value))}
                  style={inputStyle}
                  placeholder="0.00"
                />
                <span style={{ fontSize: '0.875rem', fontWeight: 500, padding: '0 4px' }}>
                  ${(item.quantity * item.rate).toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={() => removeLineItem(item.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    fontSize: '1.25rem',
                    padding: '4px',
                  }}
                  title="Remove item"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addLineItem}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              background: 'transparent',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-muted)',
              fontSize: '0.8125rem',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            + Add Line Item
          </button>

          {/* Totals */}
          <div style={{ marginTop: '20px', borderTop: '1px solid var(--color-border)', paddingTop: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '24px', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Subtotal</span>
                <span style={{ fontWeight: 500, minWidth: '80px', textAlign: 'right' }}>${subtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', gap: '24px', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Tax (8%)</span>
                <span style={{ fontWeight: 500, minWidth: '80px', textAlign: 'right' }}>${tax.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', gap: '24px', fontSize: '1.125rem', fontWeight: 700 }}>
                <span>Total</span>
                <span style={{ minWidth: '80px', textAlign: 'right' }}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <label style={labelStyle}>Notes / Terms</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Payment terms, additional notes for the client..."
            rows={4}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Link
            to="/invoices"
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              fontSize: '0.875rem',
              textDecoration: 'none',
            }}
          >
            Cancel
          </Link>
          <button
            type="submit"
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              fontSize: '0.875rem',
            }}
          >
            Save Draft
          </button>
          <button
            type="submit"
            style={{
              padding: '10px 20px',
              background: 'var(--color-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: '#fff',
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            Send Invoice
          </button>
        </div>
      </form>
    </div>
  );
}
